import { useState, type FormEvent } from 'react';
import { api, ApiError } from '~/lib/api';
import { cn } from '~/lib/cn';
import type { Cardholder, CardholderType } from '~/lib/cardholders';
import { NIGERIAN_STATES } from '~/lib/nigeria';
import { SlideOver } from '~/components/ui/SlideOver';

interface NewCardholderPanelProps {
  open: boolean;
  onClose: () => void;
  onCreated: (c: Cardholder) => void;
}

interface Draft {
  externalRef: string;
  firstName: string;
  lastName: string;
  nameOnCard: string;
  email: string;
  phoneNumber: string;
  accountNumber: string;
  accountName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  type: CardholderType;
}

function emptyDraft(): Draft {
  return {
    externalRef: '',
    firstName: '',
    lastName: '',
    nameOnCard: '',
    email: '',
    phoneNumber: '',
    accountNumber: '',
    accountName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    type: 'individual',
  };
}

type Errors = Partial<Record<keyof Draft, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+0-9 \-()]{6,32}$/;

function validate(d: Draft): Errors {
  const e: Errors = {};
  if (!d.externalRef.trim()) e.externalRef = 'Required';
  if (!d.firstName.trim()) e.firstName = 'Required';
  if (!d.lastName.trim()) e.lastName = 'Required';
  if (!d.nameOnCard.trim()) e.nameOnCard = 'Required';
  if (!EMAIL_RE.test(d.email.trim())) e.email = 'Needs a valid email';
  if (!PHONE_RE.test(d.phoneNumber.trim())) e.phoneNumber = 'Looks like a phone';
  if (!d.accountNumber.trim()) e.accountNumber = 'Required';
  if (!d.accountName.trim()) e.accountName = 'Required';
  if (!d.addressLine1.trim()) e.addressLine1 = 'Required';
  if (!d.city.trim()) e.city = 'Required';
  if (!d.state.trim()) e.state = 'Pick a state';
  if (!d.postalCode.trim()) e.postalCode = 'Required';
  return e;
}

/**
 * Collects the 14 fields a cardholder record requires. Grouped into three
 * sections (Person · Bank · Address) because dropping the full form on one
 * screen reads as a wall of text. Submit path:
 *   POST /dashboard/customers → kycStatus starts 'unverified'. Downstream
 *   issuance uses the same row later.
 *
 * On success parent gets the created Cardholder and refreshes its list.
 */
export function NewCardholderPanel({
  open,
  onClose,
  onCreated,
}: NewCardholderPanelProps): JSX.Element {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  function update(patch: Partial<Draft>): void {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function reset(): void {
    setDraft(emptyDraft());
    setErrors({});
    setServerError(null);
    setSubmitting(false);
  }

  function closeAndReset(): void {
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setServerError(null);
    const found = validate(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setServerError('Fix the highlighted fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api<Cardholder>('/dashboard/customers', {
        method: 'POST',
        body: {
          externalRef: draft.externalRef.trim(),
          firstName: draft.firstName.trim(),
          lastName: draft.lastName.trim(),
          nameOnCard: draft.nameOnCard.trim(),
          email: draft.email.trim(),
          phoneNumber: draft.phoneNumber.trim(),
          accountNumber: draft.accountNumber.trim(),
          accountName: draft.accountName.trim(),
          addressLine1: draft.addressLine1.trim(),
          ...(draft.addressLine2.trim() && {
            addressLine2: draft.addressLine2.trim(),
          }),
          city: draft.city.trim(),
          state: draft.state.trim(),
          postalCode: draft.postalCode.trim(),
          type: draft.type,
        },
      });
      onCreated(res);
      closeAndReset();
    } catch (err) {
      setServerError(
        err instanceof ApiError
          ? err.message
          : 'Could not create the cardholder. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SlideOver
      open={open}
      onClose={closeAndReset}
      title="New cardholder"
      description="The individual or business a card will be issued to. Details land as kycStatus='unverified' until a reviewer bumps them."
      width={520}
      footer={
        <div className="flex flex-col gap-2">
          {serverError && (
            <div
              role="alert"
              className="rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] leading-[18px] text-stop"
            >
              {serverError}
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={closeAndReset}
              className="h-9 rounded-md px-3 text-[14px] font-medium text-ink-secondary hover:bg-surface-margin hover:text-ink-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="new-cardholder-form"
              disabled={submitting}
              className="inline-flex h-9 items-center justify-center rounded-md bg-ink-primary px-4 text-[14px] font-semibold text-ink-on-dark hover:bg-ink-primary/90 disabled:opacity-60"
            >
              {submitting ? 'Creating…' : 'Create cardholder'}
            </button>
          </div>
        </div>
      }
    >
      <form id="new-cardholder-form" onSubmit={onSubmit} className="flex flex-col gap-6">
        <Section
          title="Person"
          description="Who the card is for. nameOnCard is what's embossed, so match ID exactly."
        >
          <Field label="External ref" error={errors.externalRef} hint="Your own id for this cardholder. Any string; must be unique in your tenant.">
            <TextInput value={draft.externalRef} onChange={(v) => update({ externalRef: v })} placeholder="emp-00231" invalid={!!errors.externalRef} autoFocus />
          </Field>
          <Field label="Type" hint="Individual is the default; use business for a corporate card.">
            <SegmentedType value={draft.type} onChange={(v) => update({ type: v })} />
          </Field>
          <TwoCol>
            <Field label="First name" error={errors.firstName}>
              <TextInput value={draft.firstName} onChange={(v) => update({ firstName: v })} autoComplete="given-name" invalid={!!errors.firstName} />
            </Field>
            <Field label="Last name" error={errors.lastName}>
              <TextInput value={draft.lastName} onChange={(v) => update({ lastName: v })} autoComplete="family-name" invalid={!!errors.lastName} />
            </Field>
          </TwoCol>
          <Field label="Name on card" error={errors.nameOnCard} hint="26 characters max in prod; the issuer trims if longer.">
            <TextInput value={draft.nameOnCard} onChange={(v) => update({ nameOnCard: v })} maxLength={26} invalid={!!errors.nameOnCard} />
          </Field>
          <TwoCol>
            <Field label="Email" error={errors.email}>
              <TextInput value={draft.email} onChange={(v) => update({ email: v })} type="email" inputMode="email" autoComplete="email" invalid={!!errors.email} />
            </Field>
            <Field label="Phone" error={errors.phoneNumber}>
              <TextInput value={draft.phoneNumber} onChange={(v) => update({ phoneNumber: v })} type="tel" inputMode="tel" autoComplete="tel" placeholder="+2348012345678" invalid={!!errors.phoneNumber} />
            </Field>
          </TwoCol>
        </Section>

        <Section
          title="Bank"
          description="A linked bank account on the cardholder's name. Used for refunds and settlement."
        >
          <TwoCol>
            <Field label="Account number" error={errors.accountNumber}>
              <TextInput value={draft.accountNumber} onChange={(v) => update({ accountNumber: v.replace(/[^0-9]/g, '').slice(0, 10) })} inputMode="numeric" maxLength={10} invalid={!!errors.accountNumber} />
            </Field>
            <Field label="Account name" error={errors.accountName}>
              <TextInput value={draft.accountName} onChange={(v) => update({ accountName: v })} invalid={!!errors.accountName} />
            </Field>
          </TwoCol>
        </Section>

        <Section
          title="Address"
          description="Where the cardholder lives or works. Must be a real street address — not a PO Box."
        >
          <Field label="Street address" error={errors.addressLine1}>
            <TextInput value={draft.addressLine1} onChange={(v) => update({ addressLine1: v })} autoComplete="address-line1" invalid={!!errors.addressLine1} />
          </Field>
          <Field label="Address line 2" hint="Apartment, suite, or landmark — optional.">
            <TextInput value={draft.addressLine2} onChange={(v) => update({ addressLine2: v })} autoComplete="address-line2" />
          </Field>
          <TwoCol>
            <Field label="City" error={errors.city}>
              <TextInput value={draft.city} onChange={(v) => update({ city: v })} autoComplete="address-level2" invalid={!!errors.city} />
            </Field>
            <Field label="State" error={errors.state}>
              <Select value={draft.state} onChange={(v) => update({ state: v })} invalid={!!errors.state}>
                <option value="" disabled>
                  Choose a state
                </option>
                {NIGERIAN_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          </TwoCol>
          <Field label="Postal code" error={errors.postalCode}>
            <TextInput value={draft.postalCode} onChange={(v) => update({ postalCode: v })} autoComplete="postal-code" invalid={!!errors.postalCode} />
          </Field>
        </Section>
      </form>
    </SlideOver>
  );
}

// ---------- form building blocks ----------

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section>
      <div className="mb-3">
        <div className="text-[14px] font-semibold text-ink-primary">{title}</div>
        {description && (
          <div className="mt-0.5 text-[13px] text-ink-watermark">{description}</div>
        )}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13.5px] font-medium text-ink-primary">{label}</span>
      {hint && <span className="text-[12.5px] text-ink-watermark">{hint}</span>}
      {children}
      {error && <span className="text-[13px] text-stop">{error}</span>}
    </label>
  );
}

function TwoCol({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

const INPUT = cn(
  'w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary',
  'focus:border-ink-primary focus:bg-surface-slip focus:outline-none focus:ring-2 focus:ring-ink-primary/15',
);

function TextInput({
  value,
  onChange,
  invalid,
  autoFocus,
  ...rest
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  autoFocus?: boolean;
  type?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric' | 'url';
  autoComplete?: string;
  placeholder?: string;
  maxLength?: number;
}): JSX.Element {
  return (
    <input
      {...rest}
      autoFocus={autoFocus}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(INPUT, 'h-9', invalid && 'border-stop/50')}
    />
  );
}

function Select({
  value,
  onChange,
  invalid,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(INPUT, 'h-9 appearance-none pr-8', invalid && 'border-stop/50')}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none' stroke='%23525252' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M1 1l4 4 4-4'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
      }}
    >
      {children}
    </select>
  );
}

function SegmentedType({
  value,
  onChange,
}: {
  value: CardholderType;
  onChange: (v: CardholderType) => void;
}): JSX.Element {
  const options: CardholderType[] = ['individual', 'business'];
  return (
    <div className="inline-flex rounded-md border border-rule-divider bg-surface-margin p-[2px]">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={cn(
            'h-7 rounded px-3 text-[13px] font-medium capitalize transition-colors',
            value === o
              ? 'bg-surface-slip text-ink-primary shadow-[0_1px_2px_rgba(10,10,10,0.08)]'
              : 'text-ink-secondary hover:text-ink-primary',
          )}
        >
          {o}
        </button>
      ))}
    </div>
  );
}
